import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
	getUsers,
	updateUser as _updateUser,
	removeUser as _removeUser,
} from '@/api/user'
import IApiResponse from '@/types/api-response'
import IList from '@/types/list-result'
import IPagination from '@/types/pagination'

interface UsersState {
	users: IUser[]
	loading: boolean
	error: string | null
	pagination?: IPagination
	updating: boolean
}

export interface IUser {
	_id: string
	full_name: string
	external_id: string
	created_at: string
	system_code: string[]
}

const initialState: UsersState = {
	loading: false,
	users: [],
	error: null,
	updating: false,
}

export const fetchUsers = createAsyncThunk<IApiResponse<IList<IUser>>, any>(
	'users/fetch_users',
	async ({ params, cb }: any) => {
		try {
			const result = await getUsers(params)
			if (typeof cb === 'function') {
				cb(result)
			}
			return result
		} catch (error: any) {
			const result = {
				success: false,
				message: error.message,
			}
			if (typeof cb === 'function') {
				cb(result)
			}
			return result
		}
	}
)

export const updateUser = createAsyncThunk<IApiResponse<null | undefined>, any>(
	'users/update_user',
	async ({ params, cb }: any) => {
		try {
			const result = await _updateUser(params)

			if (typeof cb === 'function') {
				cb(result)
			}

			return result
		} catch (error) {
			if (typeof cb === 'function') {
				cb({ success: false })
			}

			throw error
		}
	}
)

export const removeUser = createAsyncThunk<
	IApiResponse<null | undefined>,
	{ params: { [key: string]: any }, cb?: (r: IApiResponse<any>) => void }
>('users/remove_user', async ({ params, cb }) => {
	try {
		const result = await _removeUser(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

const usersSlice = createSlice({
	name: 'users',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUsers.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchUsers.fulfilled, (state, { payload }) => {
				state.loading = false
				if (payload.success) {
					state.pagination = payload.data!.pagination
					state.users = payload.data!.records
				} else {
					state.users = []
				}
			})
			.addCase(fetchUsers.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})
	},
})

export default usersSlice.reducer
